import React, { useEffect, useState } from 'react';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import { Button } from '@/components/Button';
import { SideBarModal } from '@/components/Modal';

dayjs.extend(utc);
dayjs.extend(timezone);

const DEFAULT_TZ = 'Asia/Jakarta';
const REASON_MAX = 500;
const REASON_MIN = 3;

const TZ_LABELS: Record<string, string> = {
  'Asia/Jakarta': 'WIB',
  'Asia/Makassar': 'WITA',
  'Asia/Jayapura': 'WIT',
};

export interface EditAttendanceTarget {
  logBookID: string;
  mitraName: string;
  mitraCode: string;
  /** Store name and formatted date, shown so the editor can confirm the row. */
  hub: string;
  date: string;
  /** ISO datetimes straight from the API, not the formatted display values. */
  clockInAt: string;
  clockOutAt: string | null;
  /** Store's zone — the form edits the mitra's local clock, not the viewer's. */
  timezone: string | null;
}

interface Props {
  target: EditAttendanceTarget | null;
  onClose: () => void;
  onSubmit: (payload: {
    clockInAt?: string;
    clockOutAt?: string | null;
    editReason: string;
  }) => Promise<void>;
}

/** ISO → "YYYY-MM-DDTHH:mm" on the store's clock, for datetime-local. */
function toLocalInput(iso: string | null, tz: string): string {
  if (!iso) return '';
  return dayjs(iso).tz(tz).format('YYYY-MM-DDTHH:mm');
}

/** "YYYY-MM-DDTHH:mm" read as the store's clock → ISO instant. */
function fromLocalInput(value: string, tz: string): string {
  return dayjs.tz(value, tz).toISOString();
}

/** "2026-09-21T16:12" → "16:12" for the "previously" hint. */
function inputToClock(value: string): string {
  return value.slice(11) || '-';
}

const FIELD_CLASS =
  'w-full h-10 px-3 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent';

export const EditAttendanceModal: React.FC<Props> = ({
  target,
  onClose,
  onSubmit,
}) => {
  const tz = target?.timezone || DEFAULT_TZ;
  const tzLabel = TZ_LABELS[tz] ?? tz;
  // An ongoing shift has no clock-out to correct yet — the mitra's own
  // clock-out will fill it, so the field is shown but locked.
  const ongoing = !!target && !target.clockOutAt;

  const [clockIn, setClockIn] = useState('');
  const [clockOut, setClockOut] = useState('');
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!target) return;
    setClockIn(toLocalInput(target.clockInAt, tz));
    setClockOut(toLocalInput(target.clockOutAt, tz));
    setReason('');
    setError(null);
  }, [target, tz]);

  if (!target) return null;

  const originalClockIn = toLocalInput(target.clockInAt, tz);
  const originalClockOut = toLocalInput(target.clockOutAt, tz);
  const clockInChanged = clockIn !== originalClockIn;
  const clockOutChanged = !ongoing && clockOut !== originalClockOut;
  const nothingChanged = !clockInChanged && !clockOutChanged;

  const handleSubmit = async () => {
    setError(null);

    if (!clockIn) {
      setError('Jam clock in wajib diisi.');
      return;
    }
    if (clockOut && dayjs(clockOut).valueOf() <= dayjs(clockIn).valueOf()) {
      setError('Jam clock out harus setelah jam clock in.');
      return;
    }
    if (reason.trim().length < REASON_MIN) {
      setError(`Tulis alasan perubahannya dulu, minimal ${REASON_MIN} karakter.`);
      return;
    }

    setSaving(true);
    try {
      await onSubmit({
        clockInAt: clockInChanged ? fromLocalInput(clockIn, tz) : undefined,
        clockOutAt: clockOutChanged
          ? clockOut
            ? fromLocalInput(clockOut, tz)
            : null
          : undefined,
        editReason: reason.trim(),
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <SideBarModal
      isOpen={!!target}
      onClose={onClose}
      width="lg"
      header={
        <div>
          <h2 className="text-base font-semibold text-gray-900">
            Edit Attendance
          </h2>
          <p className="text-xs text-gray-500 mt-0.5">
            {target.mitraName} · {target.mitraCode}
          </p>
        </div>
      }
      footer={
        <div className="flex items-center justify-between gap-3">
          <p className="text-xs text-gray-400">
            {nothingChanged
              ? 'Belum ada jam yang diubah.'
              : 'Perubahan tersimpan beserta alasannya.'}
          </p>
          <div className="flex gap-2 shrink-0">
            <Button variant="white" onClick={onClose} disabled={saving}>
              Batal
            </Button>
            <Button
              variant="primary"
              onClick={handleSubmit}
              disabled={saving || nothingChanged}
            >
              {saving ? 'Menyimpan...' : 'Simpan Perubahan'}
            </Button>
          </div>
        </div>
      }
    >
      <div className="p-6 space-y-5">
        {/* Which row is being corrected, so the editor can double-check */}
        <div className="flex flex-wrap items-center gap-x-2 gap-y-1 px-3.5 py-2.5 rounded-lg bg-gray-50 border border-gray-200 text-sm">
          <i className="ri-store-2-line text-[15px] text-gray-400" />
          <span
            className="font-medium text-gray-800 truncate max-w-[220px]"
            title={target.hub}
          >
            {target.hub || '-'}
          </span>
          <span className="text-gray-300">·</span>
          <span className="text-gray-600">{target.date}</span>
          <span className="ml-auto px-2 py-0.5 rounded-full bg-white border border-gray-200 text-[11px] font-semibold text-gray-500">
            Waktu {tzLabel}
          </span>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label
              htmlFor="edit-clock-in"
              className="block text-sm font-medium text-gray-700 mb-1.5"
            >
              Clock In
            </label>
            <input
              id="edit-clock-in"
              type="datetime-local"
              value={clockIn}
              onChange={(e) => setClockIn(e.target.value)}
              className={`${FIELD_CLASS} bg-white text-gray-800 ${
                clockInChanged ? 'border-purple-400' : 'border-gray-300'
              }`}
            />
            <p className="text-xs mt-1 h-4">
              {clockInChanged && (
                <span className="text-purple-600">
                  Sebelumnya {inputToClock(originalClockIn)}
                </span>
              )}
            </p>
          </div>

          <div>
            <label
              htmlFor="edit-clock-out"
              className="block text-sm font-medium text-gray-700 mb-1.5"
            >
              Clock Out
            </label>
            <input
              id="edit-clock-out"
              type="datetime-local"
              value={clockOut}
              disabled={ongoing}
              onChange={(e) => setClockOut(e.target.value)}
              className={`${FIELD_CLASS} ${
                ongoing
                  ? 'bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed'
                  : `bg-white text-gray-800 ${
                      clockOutChanged ? 'border-purple-400' : 'border-gray-300'
                    }`
              }`}
            />
            <p className="text-xs mt-1 h-4">
              {ongoing ? (
                <span className="text-gray-400">
                  Mitra masih clock in — belum ada jam yang bisa diubah.
                </span>
              ) : (
                clockOutChanged && (
                  <span className="text-purple-600">
                    Sebelumnya {inputToClock(originalClockOut)}
                  </span>
                )
              )}
            </p>
          </div>
        </div>

        <div>
          <div className="flex items-baseline justify-between mb-1.5">
            <label
              htmlFor="edit-reason"
              className="block text-sm font-medium text-gray-700"
            >
              Alasan Perubahan
            </label>
            <span className="text-xs text-gray-400 tabular-nums">
              {reason.length}/{REASON_MAX}
            </span>
          </div>
          <textarea
            id="edit-reason"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            rows={3}
            maxLength={REASON_MAX}
            placeholder="Contoh: HP mitra mati, clock-in dicatat manual by supervisor"
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm text-gray-800 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent resize-none"
          />
          <p className="text-xs text-gray-400 mt-1">
            Kesimpen buat audit, gak ditampilkan di tabel attendance.
          </p>
        </div>

        {clockInChanged && (
          <div className="flex gap-2.5 p-3 rounded-lg bg-yellow-50 border border-yellow-200 text-xs text-yellow-800">
            <i className="ri-image-edit-line text-[15px] shrink-0 mt-px" />
            <span>
              Jam clock in berubah, jadi stempel waktu di foto selfie bakal
              digenerate ulang otomatis.
            </span>
          </div>
        )}

        {error && (
          <div
            className="flex gap-2.5 p-3 rounded-lg bg-red-50 border border-red-200 text-xs font-medium text-red-600"
            role="alert"
          >
            <i className="ri-error-warning-line text-[15px] shrink-0 mt-px" />
            <span>{error}</span>
          </div>
        )}
      </div>
    </SideBarModal>
  );
};

export default EditAttendanceModal;
